import React, { useState, useEffect, useRef } from "react";
import { Row, Card, CardBody, Col } from "reactstrap";
import { setBreadcrumbItems } from "../../store/actions";
import { connect } from "react-redux";
import Select from "react-select";
import { ToastContainer, toast, Bounce } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import axios from "axios";
import * as XLSX from "xlsx";

const BulkDropStudents = (props) => {
  document.title = "Technical Hub | Bulk Drop Students";

  const breadcrumbItems = [
    { title: "Technical Hub", link: "#" },
    { title: "Student Registrations", link: "#" },
    { title: "Bulk Drop Students", link: "#" },
  ];

  const backendUrl = process.env.REACT_APP_BACKEND_URL;
  const fileInputRef = useRef(null);

  const [courses, setCourses] = useState([]);
  const [selectedCourse, setSelectedCourse] = useState(null);

  const [addedBy, setAddedBy] = useState("admin");
  const [excelFile, setExcelFile] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [rollNos, setRollNos] = useState([]);
  const [rollCount, setRollCount] = useState(0);

  // validation / payment-review state
  const [studentsWithPayment, setStudentsWithPayment] = useState([]);
  const [studentsWithoutPayment, setStudentsWithoutPayment] = useState([]);
  const [showPaymentReview, setShowPaymentReview] = useState(false);
  const [isDropping, setIsDropping] = useState(false);

  useEffect(() => {
    const curr_user = JSON.parse(localStorage.getItem("authUser"));
    console.log("[AUTH] Current user from localStorage:", curr_user);
    if (curr_user) setAddedBy(curr_user.first_name);
    fetchCourses();
    props.setBreadcrumbItems("Bulk Drop Students", breadcrumbItems);
  }, []);

  const fetchCourses = async () => {
    console.log("[COURSES] Fetching from:", `${backendUrl}get-courses`);
    try {
      const { data } = await axios.get(`${backendUrl}get-courses`);
      console.log("[COURSES] Response:", data);
      setCourses(data || []);
    } catch (error) {
      console.error("[COURSES] Error:", error.response || error);
      toast.error("Failed to load courses.");
    }
  };

  const parseExcel = (file) => {
    console.log("[EXCEL] Parsing file:", file.name, "| Size:", file.size, "bytes");
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const data = new Uint8Array(e.target.result);
        const workbook = XLSX.read(data, { type: "array" });
        console.log("[EXCEL] Sheet names:", workbook.SheetNames);

        const sheet = workbook.Sheets[workbook.SheetNames[0]];
        const rows = XLSX.utils.sheet_to_json(sheet);
        console.log("[EXCEL] Raw rows:", rows);

        const extractedRollNos = rows
          .map((row) =>
            String(row["roll_no"] || row["Roll No"] || row["RollNo"] || "").trim()
          )
          .filter(Boolean);

        console.log("[EXCEL] Extracted roll numbers:", extractedRollNos);
        setRollNos(extractedRollNos);
        setRollCount(extractedRollNos.length);

        if (extractedRollNos.length === 0) {
          toast.warning("No roll numbers found. Ensure column is named 'roll_no'");
        } else {
          toast.success(`${extractedRollNos.length} roll number(s) loaded.`);
        }
      } catch (err) {
        console.error("[EXCEL] Parse error:", err);
        toast.error("Failed to parse Excel file.");
      }
    };
    reader.onerror = (err) => {
      console.error("[EXCEL] FileReader error:", err);
      toast.error("Failed to read file.");
    };
    reader.readAsArrayBuffer(file);
  };

  const handleDownloadTemplate = () => {
    console.log("[TEMPLATE] Generating sample Excel template");
    try {
      const sampleRows = [
        { roll_no: "24P31A0501" },
        { roll_no: "24P31A0502" },
        { roll_no: "24P31A0515" },
      ];
      const worksheet = XLSX.utils.json_to_sheet(sampleRows);
      worksheet["!cols"] = [{ wch: 16 }];
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "roll_no");
      XLSX.writeFile(workbook, "bulk-drop-students-template.xlsx");
      toast.success("Template downloaded.");
    } catch (err) {
      console.error("[TEMPLATE] Error generating template:", err);
      toast.error("Failed to generate template.");
    }
  };

  const handleFileChange = (file) => {
    if (!file) return;
    console.log("[FILE] Selected:", file.name, file.type, file.size);
    const ext = file.name.split(".").pop().toLowerCase();
    if (!["xlsx", "xls"].includes(ext)) {
      console.warn("[FILE] Invalid type:", ext);
      toast.error("Only .xlsx or .xls files are allowed.");
      return;
    }
    setExcelFile(file);
    setShowPaymentReview(false);
    setStudentsWithPayment([]);
    setStudentsWithoutPayment([]);
    parseExcel(file);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files[0];
    console.log("[DROP] Dropped file:", file?.name);
    handleFileChange(file);
  };

  const handleRemoveFile = () => {
    console.log("[FILE] Removed");
    setExcelFile(null);
    setRollNos([]);
    setRollCount(0);
    setShowPaymentReview(false);
    setStudentsWithPayment([]);
    setStudentsWithoutPayment([]);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const runBulkDrop = async () => {
    const url = `${backendUrl}bulk-drop-students`;
    console.log("[DROP] POST URL:", url);
    console.log("[DROP] Body:", { course_id: selectedCourse.value, roll_nos: rollNos });

    setIsDropping(true);
    try {
      const res = await axios.post(url, {
        course_id: selectedCourse.value,
        roll_nos: rollNos,
      });
      console.log("[DROP] Response status:", res.status);
      console.log("[DROP] Response data:", res.data);

      toast.success("Students dropped successfully.");
      handleFullReset();
    } catch (error) {
      console.error("[DROP] Error:", error);
      console.error("[DROP] Status:", error.response?.status);
      console.error("[DROP] Response data:", error.response?.data);
      const msg = error.response?.data?.message || "Failed to drop students.";
      toast.error(msg);
    } finally {
      setIsDropping(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    console.log("[SUBMIT] Form submitted");

    if (!selectedCourse) { toast.error("Please select a course."); return; }
    if (!excelFile) { toast.error("Please upload an Excel file."); return; }
    if (rollNos.length === 0) { toast.error("No valid roll numbers found in Excel."); return; }

    console.log("[VALIDATE] course:", selectedCourse.value);
    console.log("[VALIDATE] roll_nos:", rollNos);

    const url = `${backendUrl}validate-bulk-drop-students`;
    console.log("[VALIDATE] POST URL:", url);

    setIsSubmitting(true);
    setShowPaymentReview(false);

    try {
      const res = await axios.post(url, {
        course_id: selectedCourse.value,
        roll_nos: rollNos,
      });

      console.log("[VALIDATE] Response status:", res.status);
      console.log("[VALIDATE] Response data:", res.data);

      const withoutPayment = res.data?.studentsWithoutPayment || [];
      const withPayment = res.data?.studentsWithPayment || [];

      setStudentsWithoutPayment(withoutPayment);
      setStudentsWithPayment(withPayment);

      if (withPayment.length === 0) {
        // no payment records anywhere — safe to drop immediately, no confirmation
        console.log("[VALIDATE] No payment records found, auto-dropping.");
        await runBulkDrop();
      } else {
        console.log("[VALIDATE] Payment records found, showing review table.");
        setShowPaymentReview(true);
      }
    } catch (error) {
      console.error("[VALIDATE] Error:", error);
      console.error("[VALIDATE] Status:", error.response?.status);
      console.error("[VALIDATE] Response data:", error.response?.data);
      const msg = error.response?.data?.message || "Validation failed.";
      toast.error(msg);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancelReview = () => {
    console.log("[REVIEW] Cancelled by user");
    setShowPaymentReview(false);
    setStudentsWithPayment([]);
    setStudentsWithoutPayment([]);
  };

  const handleDropAnyway = async () => {
    console.log("[REVIEW] Drop Anyway confirmed by user");
    await runBulkDrop();
    setShowPaymentReview(false);
  };

  const handleFullReset = () => {
    console.log("[RESET] Form reset");
    setSelectedCourse(null);
    setExcelFile(null);
    setRollNos([]);
    setRollCount(0);
    setShowPaymentReview(false);
    setStudentsWithPayment([]);
    setStudentsWithoutPayment([]);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const customSelectStyles = {
    control: (base, state) => ({
      ...base,
      borderRadius: "8px",
      border: state.isFocused ? "1.5px solid #4f46e5" : "1.5px solid #e2e8f0",
      boxShadow: state.isFocused ? "0 0 0 3px rgba(79,70,229,0.1)" : "none",
      minHeight: "42px",
      fontSize: "14px",
      transition: "all 0.2s",
      "&:hover": { borderColor: "#4f46e5" },
    }),
    option: (base, state) => ({
      ...base,
      fontSize: "14px",
      backgroundColor: state.isSelected ? "#4f46e5" : state.isFocused ? "#eef2ff" : "white",
      color: state.isSelected ? "white" : "#1e293b",
    }),
    placeholder: (base) => ({ ...base, color: "#94a3b8", fontSize: "14px" }),
  };

  return (
    <React.Fragment>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Sora:wght@400;500;600;700&display=swap');

        .bulk-reg-wrapper * { font-family: 'Sora', sans-serif; }

        .bulk-reg-card {
          border: none;
          border-radius: 16px;
          box-shadow: 0 1px 3px rgba(0,0,0,0.06), 0 4px 24px rgba(79,70,229,0.06);
          overflow: hidden;
        }

        .bulk-reg-header {
          background: linear-gradient(135deg, #4f46e5 0%, #6366f1 50%, #818cf8 100%);
          padding: 28px 32px;
          position: relative;
          overflow: hidden;
        }

        .bulk-reg-header::before {
          content: '';
          position: absolute;
          top: -40px; right: -40px;
          width: 160px; height: 160px;
          border-radius: 50%;
          background: rgba(255,255,255,0.08);
        }

        .bulk-reg-header h4 {
          color: white;
          font-size: 22px;
          font-weight: 700;
          margin: 0 0 6px;
          position: relative; z-index: 1;
        }

        .bulk-reg-header p {
          color: rgba(255,255,255,0.75);
          font-size: 13.5px;
          margin: 0;
          position: relative; z-index: 1;
        }

        .bulk-reg-body { padding: 32px; }

        .form-label-styled {
          font-size: 13px;
          font-weight: 600;
          color: #374151;
          margin-bottom: 6px;
          display: block;
        }

        .drop-zone {
          border: 2px dashed #c7d2fe;
          border-radius: 12px;
          background: #fafbff;
          padding: 40px 24px;
          text-align: center;
          cursor: pointer;
          transition: all 0.25s ease;
        }

        .drop-zone:hover, .drop-zone.dragging {
          border-color: #4f46e5;
          background: #eef2ff;
        }

        .dz-icon {
          width: 52px; height: 52px;
          background: linear-gradient(135deg, #eef2ff, #e0e7ff);
          border-radius: 14px;
          display: flex; align-items: center; justify-content: center;
          margin: 0 auto 14px;
          font-size: 22px;
        }

        .dz-title {
          font-size: 15px;
          font-weight: 600;
          color: #1e293b;
          margin-bottom: 4px;
        }

        .dz-sub { font-size: 12.5px; color: #94a3b8; }

        .dz-browse {
          color: #4f46e5;
          font-weight: 600;
          text-decoration: underline;
          text-underline-offset: 2px;
        }

        .upload-label-row {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 6px;
        }

        .template-link {
          background: none;
          border: none;
          padding: 0;
          font-size: 12.5px;
          font-weight: 600;
          color: #4f46e5;
          cursor: pointer;
          display: inline-flex;
          align-items: center;
          gap: 5px;
          text-decoration: none;
          transition: color 0.2s;
        }

        .template-link:hover { color: #3730a3; text-decoration: underline; }

        .file-attached {
          background: linear-gradient(135deg, #f0fdf4, #dcfce7);
          border: 1.5px solid #86efac;
          border-radius: 12px;
          padding: 16px 20px;
          display: flex;
          align-items: center;
          gap: 14px;
        }

        .file-icon {
          width: 40px; height: 40px;
          background: #22c55e;
          border-radius: 10px;
          display: flex; align-items: center; justify-content: center;
          font-size: 18px; color: white; flex-shrink: 0;
        }

        .file-name {
          font-size: 14px; font-weight: 600; color: #15803d;
          white-space: nowrap; overflow: hidden; text-overflow: ellipsis; max-width: 300px;
        }

        .file-count { font-size: 12px; color: #16a34a; margin-top: 2px; }

        .remove-btn {
          margin-left: auto;
          background: none; border: none;
          color: #dc2626; font-size: 18px; cursor: pointer;
          padding: 4px 8px; border-radius: 6px; transition: background 0.2s; flex-shrink: 0;
        }

        .remove-btn:hover { background: #fee2e2; }

        .divider { height: 1px; background: #f1f5f9; margin: 24px 0; }

        .result-box {
          border-radius: 12px; overflow: hidden;
          border: 1px solid #e2e8f0; margin-top: 24px;
        }

        .result-header { background: #1e293b; padding: 14px 20px; }
        .result-header span { color: white; font-size: 14px; font-weight: 600; }

        .result-stats {
          display: grid;
          grid-template-columns: repeat(4, 1fr);
          background: white;
        }

        .stat-item {
          padding: 20px 16px; text-align: center;
          border-right: 1px solid #f1f5f9;
        }

        .stat-item:last-child { border-right: none; }

        .stat-num { font-size: 28px; font-weight: 700; line-height: 1; margin-bottom: 4px; }
        .stat-label { font-size: 11px; font-weight: 600; text-transform: uppercase; letter-spacing: 0.8px; color: #94a3b8; }

        .stat-total .stat-num { color: #1e293b; }
        .stat-success .stat-num { color: #16a34a; }
        .stat-skip .stat-num { color: #d97706; }
        .stat-fail .stat-num { color: #dc2626; }

        .submit-btn {
          background: linear-gradient(135deg, #4f46e5, #6366f1) !important;
          border: none !important; border-radius: 10px !important;
          padding: 11px 28px !important; font-size: 14px !important;
          font-weight: 600 !important; color: white !important; cursor: pointer;
          transition: all 0.25s ease !important;
          box-shadow: 0 4px 12px rgba(79,70,229,0.3) !important;
        }

        .submit-btn:hover:not(:disabled) {
          transform: translateY(-1px) !important;
          box-shadow: 0 6px 18px rgba(79,70,229,0.4) !important;
        }

        .submit-btn:disabled { opacity: 0.65 !important; cursor: not-allowed !important; }

        .reset-btn {
          background: white !important; border: 1.5px solid #e2e8f0 !important;
          border-radius: 10px !important; padding: 11px 22px !important;
          font-size: 14px !important; font-weight: 600 !important;
          color: #64748b !important; cursor: pointer; transition: all 0.2s !important;
        }

        .reset-btn:hover { border-color: #94a3b8 !important; color: #1e293b !important; }

        .spinner {
          display: inline-block; width: 14px; height: 14px;
          border: 2px solid rgba(255,255,255,0.4); border-top-color: white;
          border-radius: 50%; animation: spin 0.7s linear infinite;
          margin-right: 8px; vertical-align: middle;
        }

        @keyframes spin { to { transform: rotate(360deg); } }

        /* ---- payment review table (new) ---- */
        .payment-review-box {
          border-radius: 12px; overflow: hidden;
          border: 1px solid #fde68a; margin-top: 24px;
        }

        .payment-review-header {
          background: linear-gradient(135deg, #d97706, #f59e0b);
          padding: 14px 20px;
        }

        .payment-review-header span {
          color: white; font-size: 14px; font-weight: 600;
        }

        .payment-table-wrap { overflow-x: auto; background: white; }

        .payment-table {
          width: 100%;
          border-collapse: collapse;
          font-size: 13px;
        }

        .payment-table th {
          text-align: left;
          padding: 12px 16px;
          background: #fffbeb;
          color: #92400e;
          font-size: 11px;
          font-weight: 700;
          text-transform: uppercase;
          letter-spacing: 0.6px;
          border-bottom: 1.5px solid #fde68a;
          white-space: nowrap;
        }

        .payment-table td {
          padding: 12px 16px;
          border-bottom: 1px solid #f1f5f9;
          color: #334155;
          vertical-align: top;
        }

        .payment-table tr:last-child td { border-bottom: none; }

        .payment-table tr:hover td { background: #fffdf7; }

        .roll-cell { font-weight: 700; color: #1e293b; white-space: nowrap; }

        .term-line {
          display: flex; justify-content: space-between; gap: 12px;
          font-size: 12.5px; padding: 2px 0;
        }

        .term-line .term-name { color: #475569; }
        .term-line .term-amount { color: #16a34a; font-weight: 600; white-space: nowrap; }

        .warning-box {
          margin: 18px 20px 20px;
          background: #fffbeb;
          border: 1.5px solid #fde68a;
          border-radius: 10px;
          padding: 14px 18px;
          font-size: 13px;
          color: #92400e;
          display: flex; align-items: flex-start; gap: 10px;
        }

        .warning-box .warn-icon { font-size: 16px; flex-shrink: 0; line-height: 1.4; }

        .review-actions {
          display: flex; gap: 12px; align-items: center;
          padding: 0 20px 22px;
        }

        .drop-anyway-btn {
          background: linear-gradient(135deg, #dc2626, #ef4444) !important;
          border: none !important; border-radius: 10px !important;
          padding: 11px 24px !important; font-size: 14px !important;
          font-weight: 600 !important; color: white !important; cursor: pointer;
          transition: all 0.25s ease !important;
          box-shadow: 0 4px 12px rgba(220,38,38,0.3) !important;
        }

        .drop-anyway-btn:hover:not(:disabled) {
          transform: translateY(-1px) !important;
          box-shadow: 0 6px 18px rgba(220,38,38,0.4) !important;
        }

        .drop-anyway-btn:disabled { opacity: 0.65 !important; cursor: not-allowed !important; }
      `}</style>

      <div className="bulk-reg-wrapper">
        <ToastContainer position="top-right" autoClose={4000} hideProgressBar={false} transition={Bounce} theme="light" />

        <Row>
          <Col xl="12">
            <Card className="bulk-reg-card">
              <div className="bulk-reg-header">
                <h4>Bulk Drop Students</h4>
                <p>Upload an Excel sheet with roll numbers to drop students from a course.</p>
              </div>

              <CardBody className="bulk-reg-body">
                <form onSubmit={handleSubmit}>

                  {/* Upload Excel */}
                  <div className="upload-label-row">
                    <label className="form-label-styled" style={{ marginBottom: 0 }}>
                      Upload Excel File <span style={{ color: "#ef4444" }}>*</span>
                    </label>
                    <button type="button" className="template-link" onClick={handleDownloadTemplate}>
                      ⬇ Download Template
                    </button>
                  </div>

                  {!excelFile ? (
                    <div
                      className={`drop-zone ${isDragging ? "dragging" : ""}`}
                      onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
                      onDragLeave={() => setIsDragging(false)}
                      onDrop={handleDrop}
                      onClick={() => fileInputRef.current?.click()}
                    >
                      <div className="dz-icon">📊</div>
                      <div className="dz-title">Drop your Excel file here</div>
                      <div className="dz-sub">
                        or <span className="dz-browse">browse to upload</span> &nbsp;·&nbsp; .xlsx / .xls only
                      </div>
                      <input
                        ref={fileInputRef}
                        type="file"
                        accept=".xlsx,.xls"
                        style={{ display: "none" }}
                        onChange={(e) => handleFileChange(e.target.files[0])}
                      />
                    </div>
                  ) : (
                    <div className="file-attached">
                      <div className="file-icon">✓</div>
                      <div style={{ flex: 1, overflow: "hidden" }}>
                        <div className="file-name">{excelFile.name}</div>
                        <div className="file-count">{rollCount} roll number(s) detected</div>
                      </div>
                      <button type="button" className="remove-btn" onClick={handleRemoveFile}>✕</button>
                    </div>
                  )}

                  <div className="divider" />

                  {/* Course select only */}
                  <Row>
                    <Col md="4">
                      <div className="mb-4">
                        <label className="form-label-styled">
                          Course <span style={{ color: "#ef4444" }}>*</span>
                        </label>
                        <Select
                          value={selectedCourse}
                          onChange={(course) => {
                            console.log("[SELECT] Course:", course);
                            setSelectedCourse(course);
                            setShowPaymentReview(false);
                            setStudentsWithPayment([]);
                            setStudentsWithoutPayment([]);
                          }}
                          options={courses.map((c) => ({ value: c._id, label: c.course_name }))}
                          placeholder="Choose Course"
                          styles={customSelectStyles}
                        />
                      </div>
                    </Col>
                  </Row>

                  <div className="divider" />

                  {/* Actions */}
                  <div style={{ display: "flex", gap: "12px", alignItems: "center" }}>
                    <button
                      type="submit"
                      className="submit-btn"
                      disabled={isSubmitting || isDropping || rollCount === 0 || !selectedCourse}
                    >
                      {isSubmitting ? <><span className="spinner" />Validating...</> : "Validate & Drop"}
                    </button>

                    <button type="button" className="reset-btn" onClick={handleFullReset}>
                      ↺ Reset
                    </button>

                    {rollCount > 0 && (
                      <span style={{ fontSize: "13px", color: "#64748b" }}>
                        {rollCount} student(s) queued
                      </span>
                    )}
                  </div>

                </form>

                {/* Payment review table — shown only when students already have payments */}
                {showPaymentReview && studentsWithPayment.length > 0 && (
                  <div className="payment-review-box">
                    <div className="payment-review-header">
                      <span>⚠ Payment Records Found — Review Before Dropping</span>
                    </div>

                    <div className="payment-table-wrap">
                      <table className="payment-table">
                        <thead>
                          <tr>
                            <th>Roll No</th>
                            <th>Course</th>
                            <th>Technology</th>
                            <th>Paid Terms</th>
                          </tr>
                        </thead>
                        <tbody>
                          {studentsWithPayment.map((student, sIdx) =>
                            (student.payments || []).map((payment, pIdx) => (
                              <tr key={`${student.roll_no}-${sIdx}-${pIdx}`}>
                                <td className="roll-cell">{student.roll_no}</td>
                                <td>{payment.course_name}</td>
                                <td>{payment.technology_name}</td>
                                <td>
                                  {(payment.terms_paid || []).map((term, tIdx) => (
                                    <div className="term-line" key={tIdx}>
                                      <span className="term-name">{term.term_name}</span>
                                      <span className="term-amount">₹{term.amount}</span>
                                    </div>
                                  ))}
                                </td>
                              </tr>
                            ))
                          )}
                        </tbody>
                      </table>
                    </div>

                    <div className="warning-box">
                      <span className="warn-icon">⚠</span>
                      <span>
                        These students already have payment records. Dropping them will only update <strong>drop_status = 1</strong>.
                      </span>
                    </div>

                    <div className="review-actions">
                      <button
                        type="button"
                        className="drop-anyway-btn"
                        disabled={isDropping}
                        onClick={handleDropAnyway}
                      >
                        {isDropping ? <><span className="spinner" />Dropping...</> : "Drop Anyway"}
                      </button>

                      <button
                        type="button"
                        className="reset-btn"
                        disabled={isDropping}
                        onClick={handleCancelReview}
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                )}

              </CardBody>
            </Card>
          </Col>
        </Row>
      </div>
    </React.Fragment>
  );
};

export default connect(null, { setBreadcrumbItems })(BulkDropStudents);
